import {LoyaltyCardMembership, LoyaltyCardMembershipObjectiveProgress} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import {LoyaltyCardObjective} from "@/loyalty-card/interfaces/LoyaltyCard";
import LoyaltyCardService from "@/loyalty-card/services/LoyaltyCardService";

/**
 * How many stamps short of the threshold a stamp card counts as near it: the
 * basket of the day may complete the card before its sales are captured, so
 * the offer is put forward. The backend's "Proches du seuil" uses the same
 * distance.
 */
export const OFFER_DISTANCE = 2;

/** The progress state of a membership, as every screen reads it. */
export interface MembershipProgress {
    /** The current, not-yet-reached objective of the membership. */
    activeObjective: LoyaltyCardMembershipObjectiveProgress | null;
    isProduct: boolean;
    isPoint: boolean;
    /** A single tier rewarding a product: its counter keeps going past the threshold. */
    isStampCard: boolean;
    productThreshold: number;
    /**
     * The stamps shown on the card. On a stamp card: the current objective
     * plus the pending rewards turned back into stamps, so it goes past the
     * threshold (27/12) and below zero after an advance (−2/12).
     */
    productProgress: number;
    productRemaining: number;
    /** Stamps past the threshold (27/12 → 15). */
    productOverflow: number;
    /** Rewards reached and not offered yet. */
    rewardsReady: number;
    /**
     * A product can be offered right now. On a stamp card, at any count: the
     * card goes below zero until purchases catch up.
     */
    canOffer: boolean;
    /** A few stamps short of the threshold (stamp card), or one product short of a tier. */
    nearThreshold: boolean;
    /** The offer is worth putting forward: a reward is reached or near. */
    offerHighlighted: boolean;
    /** Stamps the card will owe once a product is offered in advance (0 without advance). */
    advanceDebt: number;
    pointThreshold: number;
    pointProgress: number;
    pointRemaining: number;
    ruleTeaser: string | null;
    /** Whether a reward is reached and waits at the counter. */
    rewardAvailable: boolean;
    /** Short "x/y" label of the current progression, or null when not applicable. */
    fillLabel: string | null;
}

/**
 * The single objective of a stamp card, or null on any other card (points,
 * several tiers), which keeps the per objective progress.
 */
const stampObjective = (objectives: LoyaltyCardObjective[]): LoyaltyCardObjective | null => {
    const objective = objectives.length === 1 ? objectives[0] : null;

    return objective
        && objective.reward?.type?.slug === "product"
        && objective.value > 0
        && Number(objective.reward?.value ?? 0) > 0
        ? objective
        : null;
};

/**
 * Derive the progress state of a loyalty card membership, for both reward
 * models: the stamp model (stamps toward a threshold) and the points model
 * (point balance toward the point threshold). Pure: everything comes from
 * the membership detail payload.
 */
export function membershipProgress(membership: LoyaltyCardMembership): MembershipProgress {
    const service = LoyaltyCardService();
    const card = membership.loyalty_card;
    const isProduct = service.hasProductRewards(card);
    const isPoint = service.hasPointRewards(card);
    const cardObjectives = card.objectives ?? [];

    // The nearest tier (lowest threshold) first; among repetitions of the
    // same tier, the most recent.
    const activeObjective = (membership.objectives ?? [])
        .filter((objective) => objective.reached_at === null)
        .sort((a, b) => (a.objective?.value ?? 0) - (b.objective?.value ?? 0) || b.id - a.id)[0] ?? null;

    const rewardsReady = Number(membership.unit_balance ?? 0);
    // The progress column is a decimal the backend serves as a string ("3.00").
    const activeValue = Number(activeObjective?.value ?? 0);
    const stamp = stampObjective(cardObjectives);

    let productThreshold: number;
    let productProgress: number;
    let rewardAvailable: boolean;
    let canOffer: boolean;
    let nearThreshold: boolean;

    if (stamp) {
        const rewardValue = Number(stamp.reward?.value ?? 1);

        productThreshold = Number(stamp.value);
        productProgress = Math.floor(activeValue + rewardsReady * productThreshold / rewardValue);
        rewardAvailable = productProgress >= productThreshold;
        nearThreshold = !rewardAvailable && productProgress >= productThreshold - OFFER_DISTANCE;
        canOffer = rewardValue > 0;
    } else {
        // Several tiers: each objective keeps its own progress, capped at its
        // threshold, and the advance only opens one product short of it.
        const productRewards = new Map(cardObjectives
            .filter((objective) => objective.reward?.type?.slug === "product")
            .map((objective) => [objective.id, Number(objective.reward?.value ?? 0)]));

        productThreshold = Number(activeObjective?.objective?.value ?? 0);
        productProgress = Math.min(activeValue, productThreshold || Number.MAX_SAFE_INTEGER);
        const advanceUnits = (membership.objectives ?? [])
            .filter((objective) => objective.reached_at === null && objective.objective)
            .filter((objective) => {
                const threshold = Number(objective.objective?.value ?? 0);
                const value = Number(objective.value);

                return threshold > 0 && value >= threshold - 1 && value < threshold;
            })
            .reduce((sum, objective) => sum + (productRewards.get(objective.objective!.id) ?? 0), 0);
        rewardAvailable = rewardsReady >= 1;
        nearThreshold = advanceUnits > 0;
        canOffer = rewardsReady >= 1 || advanceUnits > 0;
    }

    const pointThreshold = card.point?.value ?? 0;
    const pointProgress = membership.point_balance ?? 0;

    let fillLabel: string | null = null;

    if (isProduct && productThreshold > 0) {
        fillLabel = `${productProgress}/${productThreshold}`;
    } else if (isPoint && pointThreshold > 0) {
        fillLabel = `${pointProgress}/${pointThreshold}`;
    }

    return {
        activeObjective,
        isProduct,
        isPoint,
        isStampCard: !!stamp,
        productThreshold,
        productProgress,
        productRemaining: Math.max(0, productThreshold - productProgress),
        productOverflow: Math.max(0, productProgress - productThreshold),
        rewardsReady,
        canOffer: isProduct && canOffer,
        nearThreshold: isProduct && nearThreshold,
        offerHighlighted: isProduct && (rewardAvailable || nearThreshold),
        advanceDebt: isProduct && canOffer && !rewardAvailable ? productThreshold - productProgress : 0,
        pointThreshold,
        pointProgress,
        pointRemaining: Math.max(0, pointThreshold - pointProgress),
        ruleTeaser: activeObjective?.objective?.reward?.teaser
            ?? stamp?.reward?.teaser
            ?? card.point?.teaser
            ?? null,
        rewardAvailable: (isProduct && rewardAvailable)
            || (isPoint && pointThreshold > 0 && pointProgress >= pointThreshold),
        fillLabel
    };
}
